import { motion } from "framer-motion";
import { Mic, Play, ArrowDown } from "lucide-react";

const ARCH = "https://images.unsplash.com/photo-1675906798406-f609bc8a0168?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxzdCUyMGxvdWlzJTIwc2t5bGluZSUyMGFyY2h8ZW58MHx8fHwxNzg4OTE2ODIwfDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grain">
      <div className="absolute inset-0">
        <img src={ARCH} alt="St. Louis Gateway Arch at night" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/70 via-[#050B14]/85 to-[#050B14]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050B14] via-transparent to-[#050B14]/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-28 pb-20 w-full">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono2 text-xs sm:text-sm tracking-[0.35em] uppercase mb-6 flex flex-wrap gap-x-3 gap-y-1"
            >
              <span className="text-white">Real Stories.</span>
              <span className="text-[#0066FF]">Real People.</span>
              <span className="text-[#FF6B00]">Real Change.</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.85] uppercase"
            >
              The Other
              <br />
              Side Of
              <br />
              <span className="text-[#0066FF] blue-glow">Blue</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-4 inline-block bg-[#0066FF] px-4 py-1 -rotate-1 blue-box-glow"
            >
              <span className="font-display text-2xl sm:text-3xl tracking-widest">ST. LOUIS</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              data-testid="producer-credit"
              className="mt-4 font-mono2 text-xs sm:text-sm tracking-[0.28em] uppercase text-[#94A3B8]"
            >
              Produced by <span className="text-[#FF6B00]">Dennis Haymon</span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-7 max-w-xl text-base sm:text-lg text-[#CBD5E1] leading-relaxed"
            >
              A podcast that bridges the gap. Built on truth. Driven by respect. Focused on solutions —
              honest conversations between citizens, those who've been charged, and the people working
              for real change.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <a
                href="#episodes"
                data-testid="hero-listen-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0066FF] hover:bg-[#2B82FF] transition-colors px-7 py-3.5 font-cond font-bold uppercase tracking-wide text-white blue-box-glow"
              >
                <Play className="h-5 w-5 fill-white" /> Listen Now
              </a>
              <a
                href="#live-mic"
                data-testid="hero-mic-btn"
                className="group flex items-center gap-2 rounded-full border border-[#FF6B00] text-[#FF6B00] hover:bg-[#FF6B00] hover:text-[#050B14] transition-colors px-7 py-3.5 font-cond font-bold uppercase tracking-wide"
              >
                <Mic className="h-5 w-5" /> Grab the Mic
              </a>
            </motion.div>
          </div>

          {/* Live mic motif */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-5 flex justify-center"
          >
            <div className="relative float-slow">
              <div className="absolute -inset-8 rounded-full bg-[#0066FF]/20 blur-3xl" />
              <div className="absolute -inset-8 rounded-full bg-[#FF6B00]/10 blur-3xl" />
              <div className="relative flex flex-col items-center justify-center h-72 w-72 sm:h-80 sm:w-80 rounded-full border border-[#0066FF]/30 bg-[#0C1424]/60 backdrop-blur-sm">
                <span className="absolute inset-6 rounded-full border border-[#0066FF]/40 pulse-ring" />
                <Mic className="h-28 w-28 text-[#0066FF] blue-glow" strokeWidth={1.5} />
                <div className="mt-4 font-mono2 text-xs tracking-[0.3em] uppercase text-[#FF6B00] flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-[#FF3B30] animate-pulse" /> On Air
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-16 flex items-center gap-2 text-[#94A3B8] font-mono2 text-xs uppercase tracking-widest">
          <ArrowDown className="h-4 w-4 animate-bounce" /> The conversation changes everything
        </div>
      </div>
    </section>
  );
}
